import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Toolbar,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  FitnessCenter as FitnessIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Circle as CircleIcon,
  FileDownload as FileDownloadIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  SportsTennis as TennisIcon,
  SportsBasketball as BasketballIcon,
  SportsSoccer as FootballIcon,
  SportsVolleyball as VolleyballIcon,
  SportsCricket as CricketIcon,
  SportsHandball as BadmintonIcon,
  Sports as SportsIcon,
  Add as AddIcon,
  ExitToApp as LogoutIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useLocation } from 'react-router-dom';
import EncycdataLogo from '@components/layout/EncycdataLogo';
import PlayerFitnessSkipping from '@components/performance/PlayerFitnessSkipping';
import PlayerFitnessRunning from '@components/performance/PlayerFitnessRunning';
import PlayerFitnessWallSitup from '@components/performance/PlayerFitnessWallSitup';
import PracticeService from '@components/performance/PracticeService';
import PracticeDropShot from '@components/performance/PracticeDropShot';
import PracticeSmash from '@components/performance/PracticeSmash';
import BasicPerformanceTracking from '@components/performance/BasicPerformanceTracking';
import TrainingHomeworkPanel from '@components/performance/TrainingHomeworkPanel';
import TrainingContentPanel from '@components/performance/TrainingContentPanel';
import PracticeClear from '@components/performance/PracticeClear';
import PracticeNetPlay from '@components/performance/PracticeNetPlay';
import ViewReportsView from '@components/performance/ViewReportsView';
import ManualAssessmentFormsView from '@components/performance/ManualAssessmentFormsView';
import { getPortalPaths, type PortalMode } from '@utils/portalPaths';
import { loadPlayerProfileForUser, buildFallbackPlayerProfile } from '@utils/coachPlayerProfile';

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 64;

interface PlayerPerformancePageProps {
  portalMode?: PortalMode;
}

const PlayerPerformancePage: React.FC<PlayerPerformancePageProps> = ({ portalMode = 'player' }) => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const paths = getPortalPaths(portalMode);
  const [error, setError] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedSports, setExpandedSports] = useState<string[]>(['Badminton']); // Default to Badminton expanded
  const [expandedSkillAssessmentSports, setExpandedSkillAssessmentSports] = useState<string[]>([]);
  const [playerProfile, setPlayerProfile] = useState<any | null>(null);
  const [loadingPlayer, setLoadingPlayer] = useState(portalMode === 'coach');
  
  // Evaluation mode is now route-driven (self/coach under each level in sidebar)
  const urlParams = new URLSearchParams(location.search);
  const [selectedCoach, setSelectedCoach] = useState<string>('self');
  const [coachOptions, setCoachOptions] = useState<Array<{id: string, name: string}>>([]);
  
  // Sport selection - will be set after player loads
  const [selectedSport, setSelectedSport] = useState<string>(
    urlParams.get('sport') || 'Badminton'
  );
  
  // Always player-as-self on this page; coach evaluating students uses CoachPlayerTrainingPage.

  const buildStaticPlayer = (profileData?: any) => {
    const source = profileData || user;
    return {
      id: profileData?.id || (user as any)?.playerId || user?.id || '',
      userId: profileData?.userId || user?.id || '',
      playerIdCode: profileData?.playerIdCode || (user as any)?.playerIdCode || 'N/A',
      dateOfBirth: profileData?.dateOfBirth ?? null,
      gender: profileData?.gender ?? null,
      level: profileData?.level ?? null,
      ageCategory: profileData?.ageCategory ?? null,
      primarySport: profileData?.primarySport ?? null,
      secondarySport: profileData?.secondarySport ?? null,
      country: profileData?.country ?? null,
      academyName: profileData?.academyName ?? null,
      createdAt: profileData?.createdAt ? new Date(profileData.createdAt) : new Date(),
      updatedAt: profileData?.updatedAt ? new Date(profileData.updatedAt) : new Date(),
      user: {
        id: profileData?.user?.id || user?.id || '',
        firstName: profileData?.user?.firstName || profileData?.firstName || (source as any)?.firstName || '',
        lastName: profileData?.user?.lastName || profileData?.lastName || (source as any)?.lastName || '',
        email: profileData?.user?.email || user?.email || '',
        role: user?.role,
        isActive: profileData?.user?.isActive ?? (user as any)?.isActive ?? true,
      },
    };
  };

  const player = portalMode === 'coach'
    ? buildStaticPlayer(playerProfile ?? undefined)
    : buildStaticPlayer();

  // Load player profile for coach-as-player portal
  useEffect(() => {
    if (portalMode !== 'coach' || !user?.id) return;

    const loadPlayerDetails = async () => {
      try {
        setLoadingPlayer(true);
        setError(null);
        const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
        const profile = await loadPlayerProfileForUser(user.id, token, true, user);
        if (profile) {
          setPlayerProfile(profile);
        } else {
          setPlayerProfile(buildFallbackPlayerProfile(user));
        }
      } catch (err: any) {
        console.error('Error loading player details:', err);
        setPlayerProfile(buildFallbackPlayerProfile(user));
      } finally {
        setLoadingPlayer(false);
      }
    };

    loadPlayerDetails();
  }, [portalMode, user?.id]);

  // Load coach options from both fitness entries AND skill assessment evaluations
  const loadCoachOptions = async () => {
    try {
      const coachMap = new Map<string, { id: string; name: string }>();

      // Preferred: load coaches assigned/linked to this player (even if no evaluations exist yet)
      try {
        const linkedCoachesResponse = await fetch(`/api/players/${player.id}/coaches`);
        const linkedCoachesData = await linkedCoachesResponse.json();
        if (linkedCoachesData.success && Array.isArray(linkedCoachesData.data)) {
          linkedCoachesData.data.forEach((c: any) => {
            if (c?.id) {
              const name =
                (c.name && String(c.name).trim()) ||
                `${c.firstName || ''} ${c.lastName || ''}`.trim() ||
                c.coachIdCode ||
                `Coach ${String(c.id).substring(0, 8)}`;
              coachMap.set(String(c.id), { id: String(c.id), name });
            }
          });
        }
      } catch (error) {
        // Non-fatal; fallback below will try to infer coaches from existing evaluations
        console.error('Error loading linked coaches for player:', error);
      }
      
      // Load coaches from fitness entries
      try {
        const fitnessResponse = await fetch(`/api/performance/fitness/player/${player.id}`);
        const fitnessData = await fitnessResponse.json();
        
        if (fitnessData.success && fitnessData.data) {
          fitnessData.data.forEach((entry: any) => {
            if (entry.coachId && entry.coachName) {
              coachMap.set(entry.coachId, {
                id: entry.coachId,
                name: entry.coachName
              });
            }
          });
        }
      } catch (error) {
        console.error('Error loading coaches from fitness entries:', error);
      }
      
      // Load coaches from skill assessment evaluations (all sports)
      const sports = ['Badminton', 'Cricket', 'Tennis', 'Football', 'Athletics'];
      const coachIdsFromEvals = new Set<string>();
      
      for (const sport of sports) {
        try {
          // Fetch all evaluations for this player and sport (without coachId filter to get all coaches)
          const evalResponse = await fetch(`/api/performance/basic/player/${player.id}?sport=${sport}`);
          const evalData = await evalResponse.json();
          
          if (evalData.success && evalData.data) {
            evalData.data.forEach((evaluation: any) => {
              if (evaluation.coachId && !coachMap.has(evaluation.coachId)) {
                // Collect coach IDs - don't set temporary name yet
                coachIdsFromEvals.add(evaluation.coachId);
              }
            });
          }
        } catch (error) {
          console.error(`Error loading coaches from ${sport} evaluations:`, error);
        }
      }
      
      // Fetch actual coach names for all coach IDs (both from fitness and evaluations)
      if (coachIdsFromEvals.size > 0 || coachMap.size > 0) {
        try {
          const coachesResponse = await fetch('/api/coaches');
          const coachesData = await coachesResponse.json();
          
          if (coachesData.success && coachesData.data) {
            // Create a map of all coaches by ID for quick lookup
            const coachesById = new Map<string, any>();
            coachesData.data.forEach((coach: any) => {
              coachesById.set(coach.id, coach);
            });
            
            // Update existing coaches in map (from fitness entries)
            coachMap.forEach((coachInfo, coachId) => {
              if (coachesById.has(coachId)) {
                const coach = coachesById.get(coachId);
                // Backend API returns: firstName/lastName on coach object OR in user object
                const firstName = coach.firstName || coach.user?.firstName || '';
                const lastName = coach.lastName || coach.user?.lastName || '';
                const name = `${firstName} ${lastName}`.trim() || coach.coachIdCode || coach.coach_id_code || `Coach ${coachId.substring(0, 8)}`;
                coachMap.set(coachId, {
                  id: coachId,
                  name: name
                });
              }
            });
            
            // Add new coaches found in evaluations
            coachIdsFromEvals.forEach((coachId) => {
              if (coachesById.has(coachId)) {
                const coach = coachesById.get(coachId);
                // Backend API returns: firstName/lastName on coach object OR in user object
                const firstName = coach.firstName || coach.user?.firstName || '';
                const lastName = coach.lastName || coach.user?.lastName || '';
                const name = `${firstName} ${lastName}`.trim() || coach.coachIdCode || coach.coach_id_code || `Coach ${coachId.substring(0, 8)}`;
                console.log(`[PlayerPerformance] Setting coach name for ${coachId}:`, name, 'from coach object:', {
                  firstName: coach.firstName,
                  lastName: coach.lastName,
                  user_firstName: coach.user?.firstName,
                  user_lastName: coach.user?.lastName,
                  coachIdCode: coach.coachIdCode,
                  coach_id_code: coach.coach_id_code
                });
                coachMap.set(coachId, {
                  id: coachId,
                  name: name
                });
              } else {
                // Fallback: use coach ID code or a generic name
                console.warn(`[PlayerPerformance] Coach ${coachId} not found in coaches API`);
                coachMap.set(coachId, {
                  id: coachId,
                  name: `Coach ${coachId.substring(0, 8)}`
                });
              }
            });
          } else {
            // If coaches API fails, set fallback names
            coachIdsFromEvals.forEach(coachId => {
              if (!coachMap.has(coachId)) {
                coachMap.set(coachId, {
                  id: coachId,
                  name: `Coach ${coachId.substring(0, 8)}`
                });
              }
            });
          }
        } catch (error) {
          console.error('Error loading coach names:', error);
          // Fallback: set temporary names for coaches not found
          coachIdsFromEvals.forEach(coachId => {
            if (!coachMap.has(coachId)) {
              coachMap.set(coachId, {
                id: coachId,
                name: `Coach ${coachId.substring(0, 8)}`
              });
            }
          });
        }
      }
      
      const coachOptionsList = Array.from(coachMap.values());
      setCoachOptions(coachOptionsList);
      console.log('[PlayerPerformance] Loaded coach options:', coachOptionsList);
    } catch (error) {
      console.error('Error loading coach options:', error);
    }
  };


  // Define available sports - can be extended later
  const availableSports = ['Badminton', 'Cricket', 'Tennis', 'Football', 'Athletics'];
  
  // Sports that are coming soon (should be greyed out and non-collapsible)
  const comingSoonSports = ['Cricket', 'Tennis', 'Football', 'Athletics'];
  
  // Get sport and assessment type from URL
  const pathParts = location.pathname.split('/').filter(Boolean);
  const performanceIndex = pathParts.indexOf('performance');
  const sportFromPath = performanceIndex >= 0 && pathParts[performanceIndex + 1] ? pathParts[performanceIndex + 1] : 'Badminton';
  const assessmentTypeFromPath = performanceIndex >= 0 && pathParts[performanceIndex + 2] ? pathParts[performanceIndex + 2] : 'skill-assessment-beginner-self';
  
  // Set selected sport from URL
  useEffect(() => {
    if (availableSports.includes(sportFromPath)) {
      setSelectedSport(sportFromPath);
    } else {
      setSelectedSport('Badminton');
    }
  }, [sportFromPath]);

  useEffect(() => {
    if (sportFromPath && !expandedSports.includes(sportFromPath)) {
      setExpandedSports((prev) => [...prev, sportFromPath]);
    }
  }, [sportFromPath, expandedSports]);

  // Load coach options on mount and when coach profile is updated
  useEffect(() => {
    if (player.id) {
      loadCoachOptions();
    }
    
    // Listen for coach profile updates to refresh coach names
    const handleCoachProfileUpdate = () => {
      console.log('[PlayerPerformance] Coach profile updated, refreshing coach options');
      if (player.id) {
        loadCoachOptions();
      }
    };
    
    window.addEventListener('coachProfileUpdated', handleCoachProfileUpdate);
    return () => {
      window.removeEventListener('coachProfileUpdated', handleCoachProfileUpdate);
    };
  }, [player.id]);

  // Handle URL parameter changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    setSelectedCoach(assessmentTypeFromPath.endsWith('-coach') ? 'coach' : 'self');
    const sportParam = urlParams.get('sport');
    if (sportParam && availableSports.includes(sportParam)) {
      setSelectedSport(sportParam);
    } else if (!sportParam && availableSports.length > 0) {
      setSelectedSport(availableSports[0]);
    }
  }, [location.search, availableSports, assessmentTypeFromPath]);
  
  // Get sport icon
  const getSportIcon = (sport: string | null) => {
    if (!sport) return <SportsIcon />;
    switch (sport.toLowerCase()) {
      case 'badminton':
        return <BadmintonIcon />;
      case 'tennis':
        return <TennisIcon />;
      case 'football':
        return <FootballIcon />;
      case 'cricket':
        return <CricketIcon />;
      case 'athletics':
        return <FitnessIcon />; // Using FitnessIcon for Athletics
      default:
        return <SportsIcon />;
    }
  };
  

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleBack = () => {
    navigate(paths.dashboard);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleToggleSport = (sport: string) => {
    // Don't allow toggling coming soon sports
    if (comingSoonSports.includes(sport)) {
      return;
    }
    setExpandedSports(prev =>
      prev.includes(sport)
        ? prev.filter(s => s !== sport)
        : [...prev, sport]
    );
  };

  const handleToggleSkillAssessment = (sport: string) => {
    setExpandedSkillAssessmentSports((prev) =>
      prev.includes(sport) ? prev.filter((s) => s !== sport) : [...prev, sport]
    );
  };

  const handleAssessmentClick = (sport: string, assessmentType: string) => {
    // Don't allow navigation to coming soon sports
    if (comingSoonSports.includes(sport)) {
      return;
    }
    navigate(paths.performanceAssessment(sport, assessmentType));
  };

  if (!user) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Please log in to view your performance data.</Alert>
      </Container>
    );
  }

  if (portalMode === 'coach' && (loadingPlayer || !player.id)) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  console.log('Player object for performance view:', player);

  // New training model: Skill Assessment parent with 4 levels.
  // For now all levels reuse the existing low-level design/data.
  const skillAssessmentLevels = [
    { id: 'skill-assessment-beginner', label: 'Beginner', icon: <AssessmentIcon /> },
    { id: 'skill-assessment-intermediate', label: 'Intermediate', icon: <AssessmentIcon /> },
    { id: 'skill-assessment-advanced', label: 'Advanced', icon: <AssessmentIcon /> },
    { id: 'skill-assessment-pro', label: 'Pro', icon: <AssessmentIcon /> },
  ];
  const trainingUtilityItems = [
    { id: 'self-practice', label: 'Self-Practice', icon: <SportsIcon /> },
    { id: 'joy-fun-learning', label: 'Enjoyment and Entertainment', icon: <SportsIcon /> },
    { id: 'coaching', label: 'Coaching', icon: <PersonIcon /> },
    { id: 'strength-conditioning', label: 'Strength and Conditioning', icon: <FitnessIcon /> },
    { id: 'mental-health', label: 'Mental Health', icon: <SportsIcon /> },
    { id: 'self-responsibility', label: 'Self Responsibility', icon: <AssignmentIcon /> },
    { id: 'rules-fitness', label: 'Player Misconduct', icon: <DescriptionIcon /> },
    { id: 'homework', label: 'Homework', icon: <AssignmentIcon /> },
    { id: 'submitted-reports', label: 'Submitted Reports', icon: <DescriptionIcon /> },
    { id: 'manual-assessment-forms', label: 'Manual Assessment Forms', icon: <FileDownloadIcon /> },
  ];

  const drawer = (
    <Box sx={{ bgcolor: '#2c3e50', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <Toolbar sx={{ 
        display: 'flex', 
        justifyContent: sidebarCollapsed ? 'center' : 'space-between', 
        alignItems: 'center', 
        minHeight: '48px !important', 
        bgcolor: '#A05AFF', 
        gap: 1,
        flexDirection: sidebarCollapsed ? 'column' : 'row',
        py: sidebarCollapsed ? 1 : 0,
      }}>
        {!sidebarCollapsed && (
          <>
            <EncycdataLogo height={28} lightBackground sx={{ mr: 1 }} />
            <Typography variant="h6" noWrap component="div" sx={{ color: '#ffffff', fontWeight: 700, flexGrow: 1 }}>
              Training Evaluation
            </Typography>
          </>
        )}
        <IconButton
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          sx={{ 
            color: '#ffffff',
            ml: sidebarCollapsed ? 0 : 'auto',
            bgcolor: sidebarCollapsed ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.2)',
            },
            width: sidebarCollapsed ? 40 : 'auto',
            height: sidebarCollapsed ? 40 : 'auto',
          }}
          size="small"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? <ChevronRightIcon sx={{ fontSize: 28 }} /> : <ChevronLeftIcon />}
        </IconButton>
      </Toolbar>
      <List>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleBack}
            sx={{
              color: '#ff9800',
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: 'rgba(255, 152, 0, 0.1)',
              }
            }}
          >
            <ListItemIcon sx={{ color: '#ff9800', minWidth: sidebarCollapsed ? 0 : 56 }}>
              <ArrowBackIcon />
            </ListItemIcon>
            {!sidebarCollapsed && (
              <ListItemText 
                primary="Back to Dashboard" 
                primaryTypographyProps={{ 
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: '#ff9800'
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
        
        {/* Sports as Parent Items */}
        {availableSports.map((sport) => {
          const isComingSoon = comingSoonSports.includes(sport);
          
          return (
            <React.Fragment key={sport}>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => !isComingSoon && handleToggleSport(sport)}
                  disabled={isComingSoon}
                  selected={sportFromPath === sport && !isComingSoon}
                  sx={{
                    color: isComingSoon ? '#95a5a6' : (sportFromPath === sport ? '#ffffff' : '#ecf0f1'),
                    bgcolor: isComingSoon ? '#34495e' : (sportFromPath === sport ? '#34495e' : 'transparent'),
                    opacity: isComingSoon ? 0.6 : 1,
                    fontWeight: sportFromPath === sport ? 700 : 600,
                    borderLeft: sportFromPath === sport && !isComingSoon ? '8px solid #ff9800' : '8px solid transparent',
                    '&:hover': {
                      bgcolor: isComingSoon ? '#34495e' : (sportFromPath === sport ? '#3d566e' : '#34495e'),
                      borderLeft: isComingSoon ? '8px solid transparent' : (sportFromPath === sport ? '8px solid #ff9800' : '8px solid #7f8c8d'),
                    },
                    '&.Mui-selected': {
                      bgcolor: '#34495e',
                      color: '#ffffff',
                      borderLeft: '8px solid #ff9800',
                      '&:hover': {
                        bgcolor: '#3d566e',
                        borderLeft: '8px solid #ff9800',
                      },
                    },
                    '&.Mui-disabled': {
                      opacity: 0.6,
                      bgcolor: '#34495e',
                      color: '#95a5a6',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: isComingSoon ? '#95a5a6' : '#ff9800', minWidth: sidebarCollapsed ? 0 : 56 }}>
                    {getSportIcon(sport)}
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <>
                      <ListItemText 
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              sx={{ 
                                fontSize: '0.9rem', 
                                fontWeight: sportFromPath === sport ? 700 : 600,
                                color: isComingSoon ? '#95a5a6' : (sportFromPath === sport ? '#ff9800' : '#ecf0f1')
                              }}
                            >
                              {sport}
                            </Typography>
                            {isComingSoon && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#95a5a6',
                                  fontStyle: 'italic',
                                  fontSize: '0.75rem',
                                }}
                              >
                                (Coming Soon)
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      {!isComingSoon && (
                        expandedSports.includes(sport) ? (
                          <ExpandLessIcon sx={{ color: sportFromPath === sport ? '#ff9800' : '#ecf0f1' }} />
                        ) : (
                          <ExpandMoreIcon sx={{ color: sportFromPath === sport ? '#ff9800' : '#ecf0f1' }} />
                        )
                      )}
                    </>
                  )}
                </ListItemButton>
              </ListItem>
              {!isComingSoon && !sidebarCollapsed && (
                <Collapse in={expandedSports.includes(sport)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {!comingSoonSports.includes(sport) && (
                      (() => {
                        const isSkillAssessmentActive = sportFromPath === sport && assessmentTypeFromPath.startsWith('skill-assessment');
                        return (
                      <ListItemButton
                        sx={{
                          pl: 4,
                          bgcolor: isSkillAssessmentActive ? '#34495e' : 'transparent',
                          color: '#bdc3c7',
                          '&:hover': { bgcolor: '#34495e' },
                          '&.Mui-selected': {
                            bgcolor: '#34495e',
                            '&:hover': { bgcolor: '#3d566e' },
                          },
                        }}
                        selected={isSkillAssessmentActive}
                        onClick={() => handleToggleSkillAssessment(sport)}
                      >
                        <ListItemText
                          primary="Skill Assessment"
                          primaryTypographyProps={{
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            color: isSkillAssessmentActive ? '#ff9800' : '#9fb3c8',
                          }}
                        />
                        {expandedSkillAssessmentSports.includes(sport) ? (
                          <ExpandLessIcon sx={{ color: isSkillAssessmentActive ? '#ff9800' : '#9fb3c8', fontSize: 18 }} />
                        ) : (
                          <ExpandMoreIcon sx={{ color: isSkillAssessmentActive ? '#ff9800' : '#9fb3c8', fontSize: 18 }} />
                        )}
                      </ListItemButton>
                        );
                      })()
                    )}
                    <Collapse in={expandedSkillAssessmentSports.includes(sport)} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {skillAssessmentLevels.map((assessment) => {
                      const selfAssessmentId = `${assessment.id}-self`;
                      const coachAssessmentId = `${assessment.id}-coach`;
                      const isLevelActive =
                        assessmentTypeFromPath === assessment.id ||
                        assessmentTypeFromPath === selfAssessmentId ||
                        assessmentTypeFromPath === coachAssessmentId;
                      const isSelfSelected =
                        sportFromPath === sport &&
                        (assessmentTypeFromPath === selfAssessmentId || assessmentTypeFromPath === assessment.id);
                      const isCoachSelected = sportFromPath === sport && assessmentTypeFromPath === coachAssessmentId;

                          return (
                            <Box key={assessment.id}>
                              <ListItem sx={{ pl: 4, py: 0.25 }}>
                                <ListItemIcon sx={{ minWidth: 26 }}>
                                  {React.cloneElement(assessment.icon, {
                                    sx: { fontSize: 15, color: isLevelActive ? '#ff9800' : '#95a5a6' }
                                  })}
                                </ListItemIcon>
                                <ListItemText
                                  primary={assessment.label}
                                  primaryTypographyProps={{
                                    fontSize: '0.88rem',
                                    fontWeight: 700,
                                    color: isLevelActive ? '#ecf0f1' : '#9fb3c8',
                                  }}
                                />
                              </ListItem>
                              <ListItemButton
                                sx={{
                                  pl: 7,
                                  bgcolor: 'transparent',
                                  color: '#bdc3c7',
                                  '&:hover': { bgcolor: '#34495e' },
                                  '&.Mui-selected': {
                                    bgcolor: 'transparent',
                                    color: '#bdc3c7',
                                    '&:hover': { bgcolor: '#34495e' },
                                  },
                                }}
                                selected={isSelfSelected}
                                onClick={() => handleAssessmentClick(sport, selfAssessmentId)}
                              >
                                <ListItemIcon sx={{ minWidth: 26 }}>
                                  <CircleIcon sx={{ fontSize: 7, color: isSelfSelected ? '#ff9800' : '#95a5a6' }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Self Evaluation"
                                  primaryTypographyProps={{
                                    fontSize: '0.82rem',
                                    fontWeight: isSelfSelected ? 700 : 600,
                                    color: '#bdc3c7',
                                  }}
                                />
                              </ListItemButton>
                              <ListItemButton
                                sx={{
                                  pl: 7,
                                  bgcolor: 'transparent',
                                  color: '#bdc3c7',
                                  '&:hover': { bgcolor: '#34495e' },
                                  '&.Mui-selected': {
                                    bgcolor: 'transparent',
                                    color: '#bdc3c7',
                                    '&:hover': { bgcolor: '#34495e' },
                                  },
                                }}
                                selected={isCoachSelected}
                                onClick={() => handleAssessmentClick(sport, coachAssessmentId)}
                              >
                                <ListItemIcon sx={{ minWidth: 26 }}>
                                  <CircleIcon sx={{ fontSize: 7, color: isCoachSelected ? '#ff9800' : '#95a5a6' }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Coach Evaluation"
                                  primaryTypographyProps={{
                                    fontSize: '0.82rem',
                                    fontWeight: isCoachSelected ? 700 : 600,
                                    color: '#bdc3c7',
                                  }}
                                />
                              </ListItemButton>
                            </Box>
                          );
                        })}
                      </List>
                    </Collapse>
                    {trainingUtilityItems.map((assessment) => {
                      const isSelected = sportFromPath === sport && assessmentTypeFromPath === assessment.id;
                      return (
                        <ListItemButton
                          key={assessment.id}
                          sx={{ 
                            pl: 4,
                            bgcolor: 'transparent',
                            color: '#bdc3c7',
                            '&:hover': {
                              bgcolor: '#34495e',
                            },
                            '&.Mui-selected': {
                              bgcolor: 'transparent',
                              color: '#bdc3c7',
                              '&:hover': {
                                bgcolor: '#34495e',
                              },
                            },
                          }}
                          selected={isSelected}
                          onClick={() => handleAssessmentClick(sport, assessment.id)}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {React.cloneElement(assessment.icon, { 
                              sx: { fontSize: 16, color: isSelected ? '#ff9800' : '#95a5a6' } 
                            })}
                          </ListItemIcon>
                          <ListItemText 
                            primary={assessment.label}
                            primaryTypographyProps={{ 
                              fontSize: '0.9rem', 
                              fontWeight: isSelected ? 700 : 600,
                              color: '#bdc3c7'
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>
      <Box sx={{ mt: 'auto', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleLogout}
            sx={{
              color: '#ff9800',
              '&:hover': {
                bgcolor: 'rgba(255, 152, 0, 0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ color: '#ff9800', minWidth: sidebarCollapsed ? 0 : 56 }}>
              <LogoutIcon />
            </ListItemIcon>
            {!sidebarCollapsed && (
              <ListItemText 
                primary="Logout"
                primaryTypographyProps={{ 
                  fontSize: '0.9rem', 
                  fontWeight: 600,
                  color: '#ff9800'
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );

  const renderContent = () => {
    const effectiveSelectedCoach = selectedCoach;
    const trainingLevelMap: Record<string, 'beginner' | 'intermediate' | 'advanced' | 'pro'> = {
      'skill-assessment-beginner': 'beginner',
      'skill-assessment-beginner-self': 'beginner',
      'skill-assessment-beginner-coach': 'beginner',
      'skill-assessment-intermediate': 'intermediate',
      'skill-assessment-intermediate-self': 'intermediate',
      'skill-assessment-intermediate-coach': 'intermediate',
      'skill-assessment-advanced': 'advanced',
      'skill-assessment-advanced-self': 'advanced',
      'skill-assessment-advanced-coach': 'advanced',
      'skill-assessment-pro': 'pro',
      'skill-assessment-pro-self': 'pro',
      'skill-assessment-pro-coach': 'pro',
    };
    const selectedTrainingLevel = trainingLevelMap[assessmentTypeFromPath];
    
    // Determine which content to show based on sport and assessment type
    switch (assessmentTypeFromPath) {
      case 'skill-assessment-beginner':
      case 'skill-assessment-beginner-self':
      case 'skill-assessment-beginner-coach':
      case 'skill-assessment-intermediate':
      case 'skill-assessment-intermediate-self':
      case 'skill-assessment-intermediate-coach':
      case 'skill-assessment-advanced':
      case 'skill-assessment-advanced-self':
      case 'skill-assessment-advanced-coach':
      case 'skill-assessment-pro':
      case 'skill-assessment-pro-self':
      case 'skill-assessment-pro-coach':
      case 'skill-assessment-master':
      case 'skill-assessment-low-level': // legacy support
      case 'skill-assessment-high-level': // legacy support
      case 'fitness-assessment': // hidden from sidebar, keep non-breaking route support
      case 'drill-assessment': // hidden from sidebar, keep non-breaking route support
        return <BasicPerformanceTracking 
          player={player} 
          coachId={undefined} 
          selectedCoach={effectiveSelectedCoach}
          assessmentType="master-training" 
          trainingLevel={selectedTrainingLevel}
          coachOptions={coachOptions} 
          selectedSport={sportFromPath}
        />;
      case 'submitted-reports':
        // Show ALL submitted reports (self + coach) for the player by default.
        // The table itself includes an "Evaluated By" column.
        return <ViewReportsView player={player} coachId={undefined} selectedSport={sportFromPath} />;
      case 'homework':
        return (
          <TrainingHomeworkPanel
            playerId={player.id}
            coachId={selectedCoach && selectedCoach !== 'self' && selectedCoach !== 'coach' ? selectedCoach : undefined}
            selectedSport={sportFromPath}
            trainingLevel={selectedTrainingLevel}
            canAssign={false}
            canSubmitFeedback
          />
        );
      case 'self-practice':
        return (
          <TrainingContentPanel
            selectedSport={sportFromPath}
            trainingLevel={selectedTrainingLevel}
            contentType="self-practice"
            title="Practice at Backyard/Home"
          />
        );
      case 'joy-fun-learning':
        return (
          <TrainingContentPanel
            selectedSport={sportFromPath}
            trainingLevel={selectedTrainingLevel}
            contentType="joy-fun-learning"
            title="Enjoyment and Entertainment"
          />
        );
      case 'coaching':
        return (
          <TrainingContentPanel
            selectedSport={sportFromPath}
            trainingLevel={selectedTrainingLevel}
            contentType="coaching"
            title="Coaching"
          />
        );
      case 'strength-conditioning':
        return (
          <TrainingContentPanel
            selectedSport={sportFromPath}
            trainingLevel={selectedTrainingLevel}
            contentType="strength-conditioning"
            title="Strength and Conditioning"
          />
        );
      case 'mental-health':
        return (
          <TrainingContentPanel
            selectedSport={sportFromPath}
            trainingLevel={selectedTrainingLevel}
            contentType="mental-health"
            title="Mental Health"
          />
        );
      case 'self-responsibility':
        return (
          <TrainingContentPanel
            selectedSport={sportFromPath}
            trainingLevel={selectedTrainingLevel}
            contentType="self-responsibility"
            title="Self Responsibility"
          />
        );
      case 'rules-fitness':
        return (
          <TrainingContentPanel
            selectedSport={sportFromPath}
            trainingLevel={selectedTrainingLevel}
            contentType="rules-fitness"
            title="Player Misconduct"
          />
        );
      case 'manual-assessment-forms':
        return <ManualAssessmentFormsView player={player} selectedSport={sportFromPath} />;
      default:
        // Default to Badminton - Skill Assessment Beginner if no valid route
        const validAssessmentIds = [
          ...skillAssessmentLevels.map((a) => a.id),
          ...skillAssessmentLevels.flatMap((a) => [`${a.id}-self`, `${a.id}-coach`]),
          ...trainingUtilityItems.map((a) => a.id),
          'skill-assessment-master',
          'skill-assessment-low-level',
          'skill-assessment-high-level',
          'fitness-assessment',
          'drill-assessment',
        ];
        if (!availableSports.includes(sportFromPath) || !validAssessmentIds.includes(assessmentTypeFromPath)) {
          navigate(paths.performanceDefault);
          return null;
        }
        return (
          <Alert severity="warning">
            Unknown assessment type: {assessmentTypeFromPath}
          </Alert>
        );
    }
  };
  
  // Redirect to default if no sport/assessment in URL
  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const performanceIndex = pathParts.indexOf('performance');
    
    // If we're at /.../performance (no sport/assessment), redirect to default
    if (performanceIndex >= 0 && pathParts.length === performanceIndex + 1) {
      navigate(paths.performanceDefault);
    }
    // If we have sport but no assessment type, redirect to default assessment
    else if (performanceIndex >= 0 && pathParts.length === performanceIndex + 2) {
      const sport = pathParts[performanceIndex + 1];
      if (availableSports.includes(sport)) {
        navigate(paths.performanceAssessment(sport, 'skill-assessment-beginner-self'));
      } else {
        navigate(paths.performanceDefault);
      }
    }
  }, [location.pathname, navigate, availableSports, paths]);

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH }, flexShrink: { sm: 0 }, transition: 'width 0.3s' }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
              bgcolor: '#e8f4f8',
              transition: 'width 0.3s'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
              bgcolor: '#e8f4f8',
              transition: 'width 0.3s',
              overflowX: 'hidden'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>


      {/* Main Content */}
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          width: { sm: `calc(100% - ${sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH}px)` },
          mt: 0, // Removed top margin since AppBar is removed
          transition: 'width 0.3s'
        }}
      >
        <Container maxWidth="xl">
          {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
          {renderContent()}
        </Container>
      </Box>
      
    </Box>
  );
};

export default PlayerPerformancePage;